// Fetch all public repos for Mustan-dev from GitHub REST API
export async function getAllRepos() {
  const response = await fetch('https://api.github.com/users/Mustan-dev/repos?per_page=100');
  if (!response.ok) throw new Error('Failed to fetch repos');
  return await response.json();
}
// Lightweight GitHub GraphQL helper for client-side use in dev.
// WARNING: Using a VITE_ prefixed token exposes it to the browser. For
// production use a server-side proxy or an Edge Function to keep the token secret.

const GITHUB_GRAPHQL = 'https://api.github.com/graphql';

// Prefer a server-side proxy when available (keep token secret). If
// VITE_GITHUB_PROXY_URL is set, requests are forwarded to that URL.
const PROXY_URL = import.meta.env.VITE_GITHUB_PROXY_URL || null;

export async function getGitHubStats(username = 'Mustan-dev') {
  const query = `
    query ($login: String!) {
      user(login: $login) {
        login
        name
        followers { totalCount }
        following { totalCount }
        repositories(first: 100, ownerAffiliations: OWNER, orderBy: {field: PUSHED_AT, direction: DESC}) {
          totalCount
          nodes {
            name
            description
            stargazerCount
            forkCount
            primaryLanguage { name }
            pushedAt
            url
          }
        }
        contributionsCollection { 
          contributionCalendar { totalContributions }
          totalCommitContributions
        }
      }
    }
  `;

  // If a proxy URL is provided, call it. The proxy should accept the
  // same GraphQL payload and forward to GitHub using a server-side secret.
  if (PROXY_URL) {
    const res = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables: { login: username } }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`GitHub proxy error: ${res.status} ${text}`);
    }

    const { data } = await res.json();
    return data?.user ?? null;
  }

  // Fallback to client-side token (development only)
  const token = import.meta.env.VITE_GITHUB_TOKEN;
  
  // If no token or proxy is available, attempt an unauthenticated REST fallback
  // for public data (rate-limited by GitHub). This allows the UI to show live
  // public repo counts/stars without requiring a PAT or proxy.
  if (!token) {
    try {
      const userRes = await fetch(`https://api.github.com/users/${username}`);
      if (!userRes.ok) return null;
      const userJson = await userRes.json();

      const reposRes = await fetch(`https://api.github.com/users/${username}/repos?per_page=100&sort=pushed`);
      if (!reposRes.ok) return {
        login: userJson.login,
        name: userJson.name,
        followers: { totalCount: userJson.followers },
        following: { totalCount: userJson.following },
        repositories: { totalCount: userJson.public_repos, nodes: [] },
        contributionsCollection: { contributionCalendar: { totalContributions: null }, totalCommitContributions: null }
      };

      const reposJson = await reposRes.json();
      const nodes = (reposJson || []).map((r) => ({
        name: r.name,
        description: r.description,
        stargazerCount: r.stargazers_count,
        forkCount: r.forks_count,
        primaryLanguage: { name: r.language },
        pushedAt: r.pushed_at,
        url: r.html_url,
      }));

      return {
        login: userJson.login,
        name: userJson.name,
        followers: { totalCount: userJson.followers },
        following: { totalCount: userJson.following },
        repositories: { totalCount: userJson.public_repos, nodes },
        contributionsCollection: { contributionCalendar: { totalContributions: null }, totalCommitContributions: null }
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Unauthenticated GitHub REST fallback failed:', err?.message || err);
      return null;
    }
  }

  const res = await fetch(GITHUB_GRAPHQL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query, variables: { login: username } }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`GitHub API error: ${res.status} ${text}`);
  }

  const responseJson = await res.json();
  const { data, errors } = responseJson;
  
  if (errors && errors.length > 0) {
    console.error('[githubService] GraphQL errors:', errors);
    throw new Error(`GitHub GraphQL error: ${errors[0].message}`);
  }

  return data?.user ?? null;
}
